import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { formatDate } from '@angular/common';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import * as FileSaver from 'file-saver';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';
import { buildSearchPayload, mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { SelectionAggregator } from '../../../../shared/utils/selection-aggregator';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';

import {
  LoadFinancialIncomes,
  LoadFinancialIncomesFailure,
  LoadFinancialIncomesSuccess,
  FinancialIncomeActionTypes
} from '../../store/actions/financial-income.actions';
import { SummaryFinancialIncomeModel } from '../../models/summary-financial-income.model';
import { FinancialIncomeService } from '../../services/financial-income.service';
import { createFinancialIncomeTableConfig, TableRowAction } from './financial-income-table.config';

import { ActualSelected, Utils } from '../../../../../shared/utils';
import { FinancialIncomeService as FinancialIncomeV1Service } from '../../../../../modules/invoice/financialincome/financialincome.service';

@Component({
  standalone: false,
  selector: 'app-financial-income-list',
  templateUrl: './financial-income-list.components.html',
  styleUrls: ['./financial-income-list.components.css']
})
export class FinancialIncomeListV2Component implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('invoiceSummary') invoiceSummary!: TemplateRef<any>;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;

  icon = 'trending_up';
  title = 'Ingresos financieros';
  subTitle = 'Consulta y gestiona tus ingresos financieros';

  summary: any = null;
  selectedIds: string[] = [];
  selectionTotals = new SelectionAggregator({
    totalAmount: (r: any) => r.amountWithoutTax,
    totalRetention: (r: any) => r.retention,
    totalCommissions: (r: any) => r.commissions,
  });

  monthsControl: FormControl = new FormControl([]);
  quickSearch = '';
  private readonly quickSearch$ = new Subject<string>();

  private lastState: TableStateSnapshot = {
    pageIndex: 0,
    pageSize: 10,
    filters: {},
    sortField: '',
    sortDirection: ''
  };

  private destroy$ = new Subject<void>();
  private subs: Subscription[] = [];

  constructor(
    private store: Store<any>,
    private actions$: Actions,
    private router: Router,
    private financialIncomeService: FinancialIncomeService,
    private financialIncomeV1Service: FinancialIncomeV1Service,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private loading: LoadingDialogService,
    private tier: TierService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    let defaultFilter: any = {};
    const stored = localStorage.getItem('viewSummaryFinancialIncomeFilters');
    if (stored) {
      try {
        defaultFilter = JSON.parse(stored);
        if (defaultFilter.pageSize == null) defaultFilter.pageSize = 10;
      } catch (e) { defaultFilter = {}; }
    }

    this.tableConfig = createFinancialIncomeTableConfig(
      {
        onCreate: () => this.create(),
        actionMap: {
          [TableRowAction.Edit]: (row) => this.edit(row),
          [TableRowAction.Delete]: (row) => this.delete(row),
        },
        selectable: true,
        summaryTemplate: this.invoiceSummary,
        columns: [
          { key: 'concept', label: 'Concepto', sortable: true, sortKey: 'Concept', ellipsis: true, onCellClick: (row) => this.edit(row) },
          { key: 'date', label: 'Fecha', sortable: true, sortKey: 'Date', formatFn: (val) => val ? formatDate(val, 'dd/MM/yyyy', 'es-ES') : '' },
          { key: 'entranceAccount', label: 'Cuenta contable', sortable: true, sortKey: 'EntranceAccount' },
          { key: 'amountWithoutTax', label: 'Importe bruto', sortable: true, sortKey: 'AmountWithoutTax', formatFn: (val) => val != null ? Utils.FormatMoney(val) + ' €' : '' },
          { key: 'retention', label: 'Retención', sortable: true, sortKey: 'Retention', formatFn: (val) => val != null ? Utils.FormatMoney(val) + ' €' : '' },
          { key: 'commissions', label: 'Comisiones', sortable: true, sortKey: 'Commissions', formatFn: (val) => val != null ? Utils.FormatMoney(val) + ' €' : '' },
          { key: 'paymentMethodInfo', label: 'Cuenta bancaria', sortable: false },
        ],
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.create() },
        ],
        gridActions: [
          { label: 'Excel Contabilidad', icon: 'file_download', type: 'button', callback: () => this.excelAccounting() },
          { label: 'Excel Contabilidad Global', icon: 'file_download', type: 'button', callback: () => this.excelGlobalAccounting() },
        ],
        rowConfig: {
          onToggleSelect: (row: any, selected: boolean) => {
            const id = row.uid;
            if (selected) {
              this.addSelected(id);
              this.selectionTotals.add(row);
            } else {
              this.removeSelected(id);
              this.selectionTotals.remove(row);
            }
          }
        },
      },
      {
        filterView: 'viewSummaryFinancialIncomeFilters',
        defaultFilter,
      }
    );

    this.subs.push(
      this.actions$
        .pipe(
          ofType<LoadFinancialIncomesSuccess>(FinancialIncomeActionTypes.LoadFinancialIncomesSuccess),
          map(action => action.payload)
        )
        .subscribe(res => {
          const items = ((res.items as any[]) || []).map(item => ({
            ...item,
            __selected: this.selectedIds.indexOf(item.uid) !== -1,
          }));
          this.summary = res.summary;
          this.t1.tableState.setData(items);
          this.t1.tableState.setTotalItems(res.total);
          this.t1.tableState.setLoading(false);
        })
    );

    this.subs.push(
      this.actions$
        .pipe(ofType<LoadFinancialIncomesFailure>(FinancialIncomeActionTypes.LoadFinancialIncomesFailure))
        .subscribe(() => {
          this.t1.tableState.setLoading(false);
          this.notify.error('No se pudieron cargar los ingresos financieros');
        })
    );
  }

  ngAfterViewInit(): void {
    this.tableConfig.summaryTemplate = this.invoiceSummary;
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;

    this.monthsControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: any) => {
        const raw = Array.isArray(value) ? value : [];
        const clean = raw.filter((v: any) => typeof v === 'number');
        if (clean.length !== raw.length) {
          this.monthsControl.setValue(clean, { emitEvent: false });
        }
        this.t1.tableState.patchFilters({ months: clean });
      });

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(s => (s.filters && (s.filters as any).search) || ''),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(v => { if (v !== this.quickSearch) this.quickSearch = v as string; });

    this.restoreStoredState();

    this.t1.tableState.state$
      .pipe(
        map(s => ((s.filters && (s.filters as any).months) || []) as number[]),
        distinctUntilChanged((a, b) => a.length === b.length && a.every((v, i) => v === b[i])),
        takeUntil(this.destroy$),
      )
      .subscribe(months => this.monthsControl.setValue(months, { emitEvent: false }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        this.lastState = state;
        localStorage.setItem('viewSummaryFinancialIncomeFilters', JSON.stringify(state));
        this.dispatchLoad();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subs.forEach(s => s.unsubscribe());
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  clearSelection(): void {
    this.selectedIds = [];
    this.selectionTotals.reset();
    const data = (this.t1.tableState.snapshot.data || []).map((row: any) => ({ ...row, __selected: false }));
    this.t1.tableState.setData(data);
  }

  create(): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.router.navigate(['/invoice/financialincome/create']);
  }

  edit(row: SummaryFinancialIncomeModel): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.router.navigate(['/invoice/financialincome/edit/' + row.uid]);
  }

  delete(row: SummaryFinancialIncomeModel): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.confirm.open({
      title: 'Eliminar ingreso financiero',
      subtitle: row.concept || undefined,
      message: '¿Seguro que quieres eliminar este ingreso financiero? Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.incomeDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando ingreso financiero…',
      errorText: 'No se pudo eliminar el ingreso financiero',
      action: () => this.financialIncomeV1Service.deleteData(row.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Ingreso financiero eliminado correctamente');
      this.removeSelectedRow(row);
      this.dispatchLoad();
    });
  }

  excelAccounting(): void {
    if (!this.tier.requireDocumentsTier()) return;

    const fromSelection = this.selectedIds.length > 0;
    let exported = 0;

    const task = (fromSelection ? Promise.resolve([...this.selectedIds]) : this.loadFilteredUids())
      .then(uids => {
        exported = uids.length;
        if (exported === 0) throw { empty: true };
        return this.financialIncomeV1Service.exportAccounting(uids);
      });

    const scope = fromSelection ? 'de la selección' : 'del listado';
    this.loading.run(`Generando Excel de contabilidad ${scope}…`, task)
      .then((blob: Blob) => {
        FileSaver.saveAs(blob, Utils.GetExcelName('Contabilidad_Ingresos_Financieros'));
        this.notify.ok(`Excel generado con ${exported} ${exported === 1 ? 'ingreso financiero' : 'ingresos financieros'}`);
      })
      .catch(error => {
        if (error && error.empty) {
          this.notify.warning('No hay ingresos financieros que exportar con los filtros actuales');
          return;
        }
        this.notify.error('No se pudo generar el Excel de contabilidad');
      });
  }

  excelGlobalAccounting(): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.loading.run('Generando Excel de contabilidad global…', this.financialIncomeV1Service.exportAccountingGlobal())
      .then(blob => {
        FileSaver.saveAs(blob, Utils.GetExcelName('Contabilidad_Ingresos_Financieros_Global'));
        this.notify.ok('Excel de contabilidad global generado correctamente');
      })
      .catch(() => this.notify.error('No se pudo generar el Excel de contabilidad global'));
  }

  private incomeDetails(row: SummaryFinancialIncomeModel): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.date) details.push({ label: 'Fecha', value: formatDate(row.date, 'dd/MM/yyyy', 'es-ES') });
    if (row.entranceAccount) details.push({ label: 'Cuenta contable', value: row.entranceAccount });
    details.push({ label: 'Importe bruto', value: Utils.FormatMoney(row.amountWithoutTax) + ' €' });
    return details;
  }

  private restoreStoredState(): void {
    const stored = this.tableConfig.defaultFilter as Partial<TableStateSnapshot>;
    if (!stored) return;

    const tableState = this.t1.tableState;
    if (stored.pageSize) tableState.setPageSize(stored.pageSize);
    if (stored.filters && Object.keys(stored.filters).length) tableState.setFilters(stored.filters);
    if (stored.sortField) tableState.setSort(stored.sortField, stored.sortDirection || '');
  }

  private buildFilter(): any {
    return { ...this.lastState, filters: { ...this.lastState.filters, year: ActualSelected.yearSelected } };
  }

  private dispatchLoad(): void {
    this.t1.tableState.setLoading(true);
    this.store.dispatch(new LoadFinancialIncomes({ filter: buildSearchPayload(this.buildFilter()) }));
  }

  private loadFilteredUids(): Promise<string[]> {
    return firstValueFrom(this.financialIncomeService.getBySearch({ ...buildSearchPayload(this.buildFilter()), page: 1, pageSize: 0 }))
      .then((res: any) => {
        if (!res || !res.isSuccess) throw new Error();
        return (res.values || []).map((item: any) => item.uid);
      });
  }

  private removeSelectedRow(row: SummaryFinancialIncomeModel): void {
    if (this.selectedIds.indexOf(row.uid) === -1) return;
    this.removeSelected(row.uid);
    this.selectionTotals.remove(row);
  }

  private addSelected(id: string): void {
    if (this.selectedIds.indexOf(id) === -1) this.selectedIds.push(id);
  }

  private removeSelected(id: string): void {
    const idx = this.selectedIds.indexOf(id);
    if (idx !== -1) this.selectedIds.splice(idx, 1);
  }
}
